import { formatTime } from "@/features/flight-estimator/_logic/utils";

import { CABIN_OPTIONS } from "../constants";
import { describeCostIndex } from "../cost-index";
import { fromUsd } from "../estimate";
import { formatTripDates } from "../trip-dates";
import type { AssumptionField, CityEstimate, Estimate, UsdRange, Workspace } from "../types";
import { travelerCount } from "../workspace";
import { safeUrl } from "../saved/fares-schema";

/** Where a cost line comes from, as exports label it. */
export type CostSource =
  | "live fares"
  | "venue rates"
  | "your proposals"
  | "your estimate"
  | "default rate"
  | "no rate data";

/** A flight's status when the home city is the destination, so nobody flies. */
export const FLIGHT_LOCAL = "Local, no flight";

/** A range in the display currency. */
export interface MoneyRange {
  low: number;
  high: number;
}

/** One city's headline numbers. */
export interface ExportCity {
  city: string;
  perPerson: MoneyRange;
  total: MoneyRange;
  cheapest: boolean;
  /** What the totals leave out, e.g. "flights", "hotel". */
  excludes: string[];
}

/** One home city's (or team's) flight to one destination. */
export interface ExportFlight {
  city: string;
  from: string;
  people: number;
  airports: string | null;
  airline: string | null;
  flightNumber: string | null;
  stops: string | null;
  duration: string | null;
  departs: string | null;
  arrives: string | null;
  cabin: string | null;
  /** Round-trip fare per person; null when there is no fare. */
  farePerPerson: number | null;
  subtotal: number | null;
  /** "Live fare", "Your fare", or why there is none. */
  status: string;
  link: string | null;
}

/** One cost line for one city, per person. */
export interface ExportCost {
  city: string;
  item: string;
  /** Null when there is no amount, e.g. no hotel rate data. */
  perPerson: MoneyRange | null;
  /** The line for the whole group, as it adds into the city's total: flights for the priced travelers, the rest for everyone. */
  total: MoneyRange | null;
  basis: string;
  source: CostSource;
}

/** Everything an export writes, in the display currency, built from the workspace model. */
export interface ExportModel {
  title: string;
  fileBase: string;
  currency: string;
  trip: {
    who: string;
    where: string;
    when: string;
    cabin: string;
    headcount: number;
    /** Travelers with no home city, whose flights the totals leave out. */
    withoutHomeCity: number;
  };
  linkedEvent: string | null;
  /** When fares were last checked, e.g. "Oct 22, 2026 14:05". */
  faresChecked: string | null;
  cities: ExportCity[];
  flights: ExportFlight[];
  costs: ExportCost[];
}

const slug = (text: string): string =>
  text
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

/**
 * The download name without its extension: "London-Lisbon-estimate-2026-11".
 * @param workspace - The workspace.
 * @returns A file-safe name.
 * @throws Never.
 */
export const exportFileBase = (workspace: Workspace): string => {
  // Names every city or says how many, so "Houston-to-3-cities" never reads as one route.
  const side = (cities: string[], many: string): string | null =>
    cities.length === 0 ? null : cities.length === 1 ? slug(cities[0]) : `${cities.length}-${many}`;
  const from = side(
    workspace.origins.map((origin) => origin.city),
    "home-cities"
  );
  const to = side(workspace.destinations, "cities");
  const head = from && to ? `${from}-to-${to}` : (to ?? from ?? (slug(workspace.title) || "Cost"));
  const month = workspace.dates ? `-${workspace.dates.start.slice(0, 7)}` : "";
  return `${head}-estimate${month}`;
};

const stopsOf = (stops: number, via: string[]): string =>
  stops === 0 ? "Direct" : `${stops} ${stops === 1 ? "stop" : "stops"}${via.length ? ` via ${via.join(", ")}` : ""}`;

const excludesOf = (city: CityEstimate, hasOrigins: boolean): string[] =>
  [
    !hasOrigins || city.flights.averageUsd === null ? "flights" : null,
    city.hotel.source === "none" ? "hotel" : null,
  ].filter((value): value is string => value !== null);

/**
 * Builds the rows every export format writes: the trip, each city's per-person and total range,
 * every home city's flight to each city, and each city's cost lines with where they come from.
 * Amounts are converted to the display currency and rounded to whole units.
 * @param input - The workspace, its estimate, FX rates, the linked event's name and when fares were checked.
 * @returns The export model.
 * @throws Never.
 */
export const buildExportModel = (input: {
  workspace: Workspace;
  estimate: Estimate;
  rates: Readonly<Record<string, number>>;
  linkedEvent: string | null;
  faresChecked: Date | null;
}): ExportModel => {
  const { workspace, estimate, rates } = input;
  const converted = fromUsd(1, workspace.currency, rates) !== null;
  const currency = converted ? workspace.currency : "USD";
  const money = (usd: number) => Math.round(converted ? (fromUsd(usd, workspace.currency, rates) ?? usd) : usd);
  const range = (usd: UsdRange): MoneyRange => ({ low: money(usd.low), high: money(usd.high) });
  // Each stay line counts for everyone, as the city's total does.
  const forGroup = (usd: UsdRange): MoneyRange =>
    range({ low: usd.low * estimate.headcount, high: usd.high * estimate.headcount });
  const hasOrigins = workspace.origins.length > 0;
  const people = travelerCount(workspace);
  const days = estimate.days;
  // "6 days", or "6 days, adjusted for Las Vegas costs" when the default rate follows the city.
  const dailyBasis = (city: CityEstimate, item: keyof CityEstimate["sources"]) =>
    city.sources[item] === "adjusted" && city.costIndex
      ? `${days} days, ${describeCostIndex(city.costIndex)}`
      : `${days} days`;
  const dailySource = (city: CityEstimate, item: keyof CityEstimate["sources"], field: AssumptionField): CostSource => {
    if (city.sources[item] === "proposals") return "your proposals";
    return workspace.assumptions.edited?.includes(field) ? "your estimate" : "default rate";
  };

  const flights = estimate.cities.flatMap((city) =>
    city.flights.routes.map((route): ExportFlight => {
      const flight = route.flight;
      const fare =
        route.usd !== null && (route.status === "priced" || route.status === "override") ? money(route.usd) : null;
      return {
        city: city.city,
        from: route.group ? `${route.group} (${route.origin})` : route.origin,
        people: route.count,
        airports: route.airports ? `${route.airports.origin}–${route.airports.destination}` : null,
        airline: flight?.airline ?? null,
        flightNumber: flight?.flightNumber ?? null,
        stops: flight ? stopsOf(flight.stops, flight.via) : null,
        duration: flight?.minutes ? formatTime(flight.minutes) : null,
        departs: flight?.departs ?? null,
        arrives: flight?.arrives ?? null,
        cabin: flight?.cabin ?? null,
        farePerPerson: fare,
        subtotal: fare === null ? null : fare * route.count,
        status:
          route.status === "priced"
            ? "Live fare"
            : route.status === "override"
              ? "Your fare"
              : route.status === "local"
                ? FLIGHT_LOCAL
                : (route.reason ?? "No fare"),
        link: safeUrl(flight?.url) ?? null,
      };
    })
  );

  const costs = estimate.cities.flatMap((city): ExportCost[] => [
    {
      city: city.city,
      item: "Flights",
      perPerson:
        city.flights.averageUsd === null
          ? null
          : range({ low: city.flights.averageUsd, high: city.flights.averageUsd }),
      total:
        city.flights.averageUsd === null
          ? null
          : range({
              low: city.flights.averageUsd * city.flights.pricedPeople,
              high: city.flights.averageUsd * city.flights.pricedPeople,
            }),
      basis: "Average round trip per person, weighted by people",
      source: "live fares",
    },
    {
      city: city.city,
      item: `Hotel, ${estimate.nights} ${estimate.nights === 1 ? "night" : "nights"}`,
      perPerson: city.hotel.source === "none" ? null : range(city.hotel.total),
      total: city.hotel.source === "none" ? null : forGroup(city.hotel.total),
      basis: city.hotel.source === "none" ? "Left out of the totals" : `${city.hotel.venueCount} venues`,
      source:
        city.hotel.source === "proposals"
          ? "your proposals"
          : city.hotel.source === "venues"
            ? "venue rates"
            : "no rate data",
    },
    {
      city: city.city,
      item: "Meeting space",
      perPerson: range(city.meetingSpace),
      total: forGroup(city.meetingSpace),
      basis: city.sources.meeting === "proposals" ? "Quoted total, split per person" : dailyBasis(city, "meeting"),
      source: dailySource(city, "meeting", "meetingSpacePerDay"),
    },
    {
      city: city.city,
      item: "Meals and activities",
      perPerson: range(city.meals),
      total: forGroup(city.meals),
      basis:
        city.sources.meals === "proposals" ? "Quoted food and beverage, split per person" : dailyBasis(city, "meals"),
      source: dailySource(city, "meals", "mealsPerDay"),
    },
  ]);

  const checked = input.faresChecked;
  return {
    title: workspace.title,
    fileBase: exportFileBase(workspace),
    currency,
    trip: {
      who: hasOrigins
        ? workspace.origins
            .map((origin) => `${origin.group ? `${origin.group}, ` : ""}${origin.city} (${origin.count})`)
            .join("; ")
        : people > 0
          ? `${people} people, home cities not set`
          : "Not set",
      where: workspace.destinations.join(", ") || "Not set",
      when: workspace.dates ? `${formatTripDates(workspace.dates)} (${estimate.nights} nights)` : "Not set",
      cabin: CABIN_OPTIONS.find((option) => option.value === workspace.cabin)?.label ?? workspace.cabin,
      headcount: people,
      withoutHomeCity: estimate.withoutHomeCity,
    },
    linkedEvent: input.linkedEvent,
    faresChecked: checked
      ? `${checked.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} ${checked.toTimeString().slice(0, 5)}`
      : null,
    cities: estimate.cities.map((city) => ({
      city: city.city,
      perPerson: range(city.perPerson),
      total: range(city.total),
      cheapest: city.city === estimate.lowestCity,
      excludes: excludesOf(city, hasOrigins),
    })),
    flights,
    costs,
  };
};
